import { Button, Grid, Table } from '@approvalmax/ui/src/components';
import { useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { AddMatchingIcon, MatchingIcon } from 'modules/sprites';
import { memo, useCallback } from 'react';
import {
    ComponentErrorBoundary,
    QBooksMatchingInfo,
    type QBooksMatchingInfoProps,
    qBooksMatchingPopupAccountLineItemState,
} from 'shared/components';
import { useCompanyBetaFeatures } from 'shared/hooks';

import { useAccountLineItems, useRequestExpanded } from '../../hooks';
import { ManualMatchingCellProps } from './BillManualMatchingCell.types';
import { useMatchingInfoNormalizer } from './BillManuaMatchingCell.hooks';

export const BillManualMatchingCell = memo<ManualMatchingCellProps>((props) => {
    const { item, onRemoveMatching } = props;

    const request = useRequestExpanded<selectors.types.QBooksBillExpandedRequest>();
    const profile = useSelector(selectors.profile.getProfileUser);

    const { isQBOMatchingOnEditing } = useCompanyBetaFeatures(request.company.id);
    const isMatchingAvailable = request.flags.isActiveReviewer || request.flags.isAuthor;
    const isRequiredDataIncomplete = !request.details.vendor?.id || !request.details.date;

    const accountLineItems = useAccountLineItems();
    const setAccountLineItem = useSetAtom(qBooksMatchingPopupAccountLineItemState);

    const matchingInfoNormalizer = useMatchingInfoNormalizer(accountLineItems);

    const handleSetAccountLineItem = useCallback(() => {
        setAccountLineItem(item as domain.QBooksAccountLineItem);
    }, [item, setAccountLineItem]);

    const handleRemoveMatching = useCallback<QBooksMatchingInfoProps['onRemoveMatching']>(
        (lineItemId) => {
            if (onRemoveMatching) {
                return onRemoveMatching(lineItemId, true);
            }

            return Promise.resolve();
        },
        [onRemoveMatching]
    );

    const showMatchingInfo = isQBOMatchingOnEditing && item.matchedLineItemId && onRemoveMatching;
    const showMatchingAvailable = isQBOMatchingOnEditing && isMatchingAvailable;
    const showMatched = item.isMatched;
    const showSpan =
        (isQBOMatchingOnEditing &&
            (isRequiredDataIncomplete || !(item as domain.QBooksAccountLineItem).account?.id || !item.amount)) ||
        !showMatched;

    return (
        <Table.Cell {...props}>
            {showMatchingInfo && (
                <ComponentErrorBoundary>
                    <QBooksMatchingInfo
                        lineItemId={item.id}
                        profileId={profile.id}
                        request={request}
                        onRemoveMatching={handleRemoveMatching}
                        matchingInfoNormalizer={matchingInfoNormalizer}
                        isNewLineItem={item.isNew}
                    />
                </ComponentErrorBoundary>
            )}

            {showMatchingAvailable && (
                <Button noPadding onClick={handleSetAccountLineItem}>
                    <AddMatchingIcon />
                </Button>
            )}

            {showMatched && (
                <Grid alignItems='center' justifyContent='center'>
                    <MatchingIcon />
                </Grid>
            )}

            {showSpan && <span />}
        </Table.Cell>
    );
});

BillManualMatchingCell.displayName = 'ManualMatchingCell';
